import { ignoreAbortError } from '@/utils/abort-util'
import { action, computed } from 'mobx'
import type {
  ListWorkspaceMembershipsFn,
  GetCurrentUserWorkspaceMembershipsFn,
} from '@/features/iam'
import { BookkeepingManagerSearchViewModel } from './BookkeepingManagerSearchViewModel'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { task } from 'mobx-task'
import {
  type BookkeepingManagerColumnDetails,
  BookkeepingManagerTableColumns,
  type ColumnId,
} from './bookkeeping-manager-table-columns'
import { type TableSortOrder } from '@/components/Table'
import { type BookkeepingManagerStore } from '@/features/admin/bookkeeping/BookkeepingManagerStore'
import { BookkeepingManagerDetailsViewModel } from '@/features/admin/bookkeeping/components/BookkeepingManagerDetails/BookkeepingManagerDetailsViewModel'
import { type BookkeepingPendingAction } from './BookkeepingPendingAction'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tap } from 'rxjs'
import { ViewModelList } from '@/infra/view-model-list'
import { BookkeepingPendingActionItemViewModel } from './BookkeepingPendingActionItemViewModel'

export class BookkeepingManagerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _items = new ViewModelList({
    models: () => this.searcher.filteredItems.slice(),
    create: (bookkeepingPendingAction: BookkeepingPendingAction) =>
      new BookkeepingPendingActionItemViewModel(
        bookkeepingPendingAction,
        this.detailsVm,
      ),
  })

  /**
   * Columns to populate the bookkeeping pending actions table header and rows with.
   */
  readonly columns = BookkeepingManagerTableColumns

  /**
   * The instance of BookkeepingManagerSearchViewModel. AdminViewModel owns its
   * activation: the admin navigation shows its total on every admin page.
   */
  readonly searcher

  /**
   * The bookkeeping manager details view model.
   */
  readonly detailsVm

  constructor(
    private readonly _bookkeepingManagerStore: BookkeepingManagerStore,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _createFilterStorage: FilterStorageFactory,
    private readonly _closeNav: () => void,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.searcher = new BookkeepingManagerSearchViewModel(
      this._bookkeepingManagerStore,
      this._createAlertRetryWrapper,
      this._listWorkspaceMemberships,
      this._getWorkspaceMemberships,
      this._createFilterStorage('Bookkeeping'),
    )

    this.detailsVm = new BookkeepingManagerDetailsViewModel(
      this._bookkeepingManagerStore,
      this._newAlert,
      this._createAlertErrorHandler,
      this._closeNav,
    )

    this._lifecycle.manage(this._items)
    this._lifecycle.manage(
      this.detailsVm.update$.pipe(
        tap((bookkeepingPendingAction: BookkeepingPendingAction) => {
          this.searcher.reloadActionFilter()
          this.searcher.removeBookkeepingPendingAction(
            bookkeepingPendingAction.id,
          )
        }),
      ),
    )
  }

  @computed
  get columnIds() {
    return this.columns.map((column) => column.id)
  }

  /**
   * The table rows.
   */
  get items() {
    return this._items.items
  }

  /**
   * Gets the sort order for the specified column.
   */
  getSortOrder(column: BookkeepingManagerColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  @action.bound
  setSortOrder(columnId: ColumnId, direction: TableSortOrder) {
    const field = this.columns.find((column) => column.id === columnId)?.field
    if (!field) {
      return
    }

    this.searcher.setSortField(field)
    this.searcher.setSortDirection(direction)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the workspaces the searcher filters by.
   */
  fetch = task(async () => {
    await this.searcher.fetchWorkspaces(this._lifecycle.signal)
  })
}
